import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";

const SITE_URL = "https://prosourcelabs.org";
const SITE_NAME = "ProSource Labs";
const BRAND_TAGLINE =
  "Premium research compounds with batch-tested quality and fast Southern California shipping. Precision. Purity. Performance.";
const DEFAULT_OG_IMAGE =
  "https://customer-assets.emergentagent.com/job_peptide-pro-6/artifacts/geglxhs1_ChatGPT%20Image%20Apr%2028%2C%202026%2C%2001_46_32%20PM.png";

/**
 * Per-page SEO meta wrapper. Renders title, description, canonical, Open Graph,
 * Twitter Card, and optional JSON-LD structured data.
 *
 * Usage:
 *   <SEO
 *     title="BPC-157 5mg — Lab-Verified Research Peptide"
 *     description="Premium BPC-157 5mg for research. 99%+ HPLC purity..."
 *     path="/product/bpc-157"
 *     image="https://.../bpc-157.jpg"
 *     jsonLd={{ "@context": "https://schema.org", "@type": "Product", ... }}
 *   />
 */
export default function SEO({
  title,
  description,
  path = "",
  image = DEFAULT_OG_IMAGE,
  type = "website",
  jsonLd,
  keywords,
  noindex = false,
  titleTemplate = "suffix", // "suffix" (default) → "{title} · ProSource Labs"; "product" → "{title} | ProSource Labs | Fast Southern California Shipping"; "raw" → use title as-is
  descriptionTagline, // Optional override for the site-wide brand tagline appended to descriptions. Pass "" to suppress entirely (used for cosmetic PDPs so "research compounds" language doesn't leak in). Pass a custom string to substitute (e.g. cosmetic tagline).
}) {
  let fullTitle;
  if (!title) {
    fullTitle = `${SITE_NAME} — Precision. Purity. Performance.`;
  } else if (titleTemplate === "product") {
    fullTitle = `${title} | ${SITE_NAME} | Fast Southern California Shipping`;
  } else if (titleTemplate === "raw") {
    fullTitle = title;
  } else {
    fullTitle = `${title} · ${SITE_NAME}`;
  }
  const canonical = `${SITE_URL}${path}`;
  // Every page description ends with the brand tagline so every SERP snippet
  // carries the same brand promise. If a page supplies no description, the
  // tagline stands alone. `descriptionTagline` prop overrides the default
  // brand tagline — cosmetic PDPs pass a cosmetic-appropriate tagline (or
  // "" to suppress) so "research compounds" language doesn't leak into the
  // Skincare category's meta descriptions.
  const tagline = descriptionTagline !== undefined ? descriptionTagline : BRAND_TAGLINE;
  const desc = description
    ? (tagline ? `${description} ${tagline}` : description)
    : (tagline || BRAND_TAGLINE);

  // Imperatively inject JSON-LD scripts into document.head. react-helmet-async
  // sometimes places script[type=application/ld+json] children outside <head>
  // depending on the SSR/hydration path, which fails the strict Google
  // rich-results requirement that structured data be inside <head>. Doing
  // it in useEffect guarantees head placement in every hydration mode and
  // cleans up when the route unmounts.
  useEffect(() => {
    const schemas = Array.isArray(jsonLd) ? jsonLd.filter(Boolean) : jsonLd ? [jsonLd] : [];
    const nodes = schemas.map((schema) => {
      const el = document.createElement("script");
      el.type = "application/ld+json";
      el.setAttribute("data-seo-jsonld", "1");
      el.text = JSON.stringify(schema);
      document.head.appendChild(el);
      return el;
    });
    return () => {
      for (const n of nodes) {
        if (n && n.parentNode) n.parentNode.removeChild(n);
      }
    };
  }, [jsonLd]);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={desc} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD structured data is injected imperatively above via
          useEffect so it always lands inside <head> (Helmet's placement of
          script children is unreliable across hydration modes). */}
    </Helmet>
  );
}

// Convenience builder for Product JSON-LD that follows Google's recommended shape.
export function buildProductJsonLd({ name, description, image, sku, brand, slug, priceFrom, priceCurrency = "USD", inStock = true, ratingValue, ratingCount, category }) {
  // Google Merchant + rich-result requirements: priceValidUntil (1 year out
  // is the sensible default), hasMerchantReturnPolicy, and shippingDetails.
  const oneYearOut = new Date();
  oneYearOut.setFullYear(oneYearOut.getFullYear() + 1);
  const priceValidUntil = oneYearOut.toISOString().slice(0, 10);

  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name,
    description,
    image,
    sku: sku || slug,
    mpn: sku || slug,
    brand: { "@type": "Brand", name: brand || SITE_NAME },
    manufacturer: { "@type": "Organization", name: brand || SITE_NAME },
    url: `${SITE_URL}/product/${slug}`,
    ...(category ? { category } : {}),
    offers: {
      "@type": "Offer",
      price: priceFrom,
      priceCurrency,
      priceValidUntil,
      availability: inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      itemCondition: "https://schema.org/NewCondition",
      url: `${SITE_URL}/product/${slug}`,
      seller: { "@type": "Organization", name: SITE_NAME },
      hasMerchantReturnPolicy: {
        "@type": "MerchantReturnPolicy",
        applicableCountry: "US",
        returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
        merchantReturnDays: 30,
        returnMethod: "https://schema.org/ReturnByMail",
        returnFees: "https://schema.org/FreeReturn",
      },
      shippingDetails: {
        "@type": "OfferShippingDetails",
        shippingRate: {
          "@type": "MonetaryAmount",
          value: "12.99",
          currency: "USD",
        },
        shippingDestination: {
          "@type": "DefinedRegion",
          addressCountry: "US",
        },
        deliveryTime: {
          "@type": "ShippingDeliveryTime",
          handlingTime: {
            "@type": "QuantitativeValue",
            minValue: 1,
            maxValue: 2,
            unitCode: "DAY",
          },
          transitTime: {
            "@type": "QuantitativeValue",
            minValue: 2,
            maxValue: 5,
            unitCode: "DAY",
          },
        },
      },
    },
    ...(ratingValue && ratingCount
      ? {
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue,
            reviewCount: ratingCount,
          },
        }
      : {}),
  };
}

// WebSite schema with SearchAction enables Google's sitelinks searchbox
// (a search field that appears under your listing in Google results).
export function buildWebSiteJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    alternateName: "ProSource Labs Peptides",
    url: SITE_URL,
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SITE_URL}/shop?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
}

// LocalBusiness / Store schema — region-only (no street address). Helps
// geo-adjacent queries and gives Google a stronger brand-entity signal.
export function buildLocalBusinessJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Store",
    name: SITE_NAME,
    url: SITE_URL,
    logo: DEFAULT_OG_IMAGE,
    image: DEFAULT_OG_IMAGE,
    description:
      "Direct-to-researcher retail store for HPLC-verified research peptides. Ships from Southern California, no purchase minimum. Optional volume discount tiers on larger orders.",
    priceRange: "$$",
    telephone: "+1-951-987-3443",
    address: {
      "@type": "PostalAddress",
      addressCountry: "US",
      addressRegion: "CA",
    },
    areaServed: {
      "@type": "Country",
      name: "United States",
    },
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer support",
      email: "support@prosourcelabs.org",
      areaServed: "US",
      availableLanguage: ["English"],
    },
  };
}

export function buildBreadcrumbJsonLd(crumbs) {
  // crumbs = [{name, path}, ...]
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: `${SITE_URL}${c.path}`,
    })),
  };
}

export function buildOrganizationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
    logo: DEFAULT_OG_IMAGE,
    description:
      "ProSource Labs is a US-based direct-to-researcher retail store for HPLC-verified research peptides. Buy a single vial, no minimum. Fast domestic shipping, optional volume discount tiers on larger orders.",
    sameAs: [],
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer support",
      email: "support@prosourcelabs.org",
      areaServed: "US",
      availableLanguage: ["English"],
    },
  };
}
